import { createStore, applyMiddleware, compose, combineReducers } from "redux";
import thunk from "redux-thunk";
import createSagaMiddleware from "redux-saga";

import socket from "./socket";
import RPCWebSocket from "./rpcSocket";
import commonReducer from "./common";
import {
  watchCommon,
  watchDashboard,
  watchBackup,
  watchRestore,
  watchAuth,
  watchAccount,
  watchActivities,
  watchUsers,
  watchVirtualization,
  watchBackupPlan
} from "./sagas";

import dashboardReducer, {
  initiateBackup,
  initiateStopBackup,
  initiateStopRestore
} from "App/Dashboard/ducks";
import backupSettingsReducer, {
  initiateFullBackup
} from "App/Dashboard/BackupSettings/ducks";
import fileRestoreReducer, {
  initiateRestore,
  enumFolder,
  createFolder,
  fetchUpdatedVolumesFR
} from "App/Restore/Tabs/body/File/ducks";
import imageRestoreReducer, {
  fetchUpdatedVolumes,
  initiateImageRestore
} from "App/Restore/Tabs/body/Image/ducks";
import virtualizationReducer, {
  logoutVirtInstances
} from "App/Virtualization/ducks";
import activitiesReducer from "App/Activities/ducks";
import appLogsReducer from "App/Dashboard/Logs/ducks";
import authReducer, { logoutUser } from "App/Auth/ducks";
import accountReducer, {
  initiateAccountActivityChange,
  initiateUpgradePlan
} from "App/Account/ducks";
import usersReducer from "App/Users/ducks";
import backupPlanReducer from "App/BackupPlan/ducks";

import { reConfig, configAppInstance } from "config";
import { accountSettingsChangedPNSStart } from "../App/Account/ducks";
import { cancelAccountPNSStart } from "../App/Account/ducks";

const composeEnhancers =
  (process.env.REACT_APP_MODE === "local" &&
    typeof window !== "undefined" &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose;

const appReducer = combineReducers({
  common: commonReducer,
  dashboard: dashboardReducer,
  backupSettings: backupSettingsReducer,
  fileRestore: fileRestoreReducer,
  imageRestore: imageRestoreReducer,
  appLogs: appLogsReducer,
  auth: authReducer,
  account: accountReducer,
  activities: activitiesReducer,
  virtualization: virtualizationReducer,
  users: usersReducer,
  backupPlan: backupPlanReducer
});

const rootReducer = (state, action) => {
  if (action.type === "LOGOUT_USER") {
    state = { auth: state.auth };
  }

  if (
    action.type === "FETCH_APP_SETTINGS_SUCCESS" ||
    action.type === "INITIATE_NEW_SOCKET_CONNECTION"
  ) {
    reConfig();
    socketCalls();
  }

  if (action.type === "FETCH_APP_SETTINGS_SUCCESS") {
    rpcSocketCalls();
  }

  if (
    action.type === "CHECK_AUTH_SUCCESS" ||
    action.type === "LOGIN_USER_SUCCESS"
  ) {
    if (
      action.message.origin ||
      (action.message.user && action.message.user.origin)
    )
      configAppInstance(
        action.message.origin ||
          (action.message.user && action.message.user.origin)
      );
  }

  return appReducer(state, action);
};

const sagaMiddleware = createSagaMiddleware();

const store = createStore(
  rootReducer,
  composeEnhancers(applyMiddleware(thunk, sagaMiddleware))
);

// Saga Middleware Watcher
sagaMiddleware.run(watchCommon);
sagaMiddleware.run(watchDashboard);
sagaMiddleware.run(watchBackup);
sagaMiddleware.run(watchRestore);
sagaMiddleware.run(watchAuth);
sagaMiddleware.run(watchAccount);
sagaMiddleware.run(watchActivities);
sagaMiddleware.run(watchUsers);
sagaMiddleware.run(watchBackupPlan);
sagaMiddleware.run(watchVirtualization);

function socketCalls() {
  const { wsConnection } = socket(store.dispatch, store.getState);

  sagaMiddleware.run(initiateBackup, { wsConnection });
  sagaMiddleware.run(initiateFullBackup, { wsConnection });
  sagaMiddleware.run(initiateStopBackup, { wsConnection });
  sagaMiddleware.run(initiateRestore, { wsConnection });
  sagaMiddleware.run(fetchUpdatedVolumesFR, { wsConnection });
  sagaMiddleware.run(fetchUpdatedVolumes, { wsConnection });
  sagaMiddleware.run(logoutUser, { wsConnection });
  sagaMiddleware.run(initiateStopRestore, { wsConnection });
  sagaMiddleware.run(initiateImageRestore, { wsConnection });
  sagaMiddleware.run(enumFolder, { wsConnection });
  sagaMiddleware.run(createFolder, { wsConnection });
  sagaMiddleware.run(initiateAccountActivityChange, { wsConnection });
  sagaMiddleware.run(initiateUpgradePlan, { wsConnection });
  sagaMiddleware.run(accountSettingsChangedPNSStart, { wsConnection });
  sagaMiddleware.run(cancelAccountPNSStart, { wsConnection });
}

function rpcSocketCalls() {
  const rpc = RPCWebSocket(store.dispatch, store.getState);

  sagaMiddleware.run(logoutVirtInstances, {
    getRpcWebSocket: rpc.getWebSocket
  });
}

export default store;
